/**
 * Touch drivers: repeat touches, loops, stuck claims, duplicate entries and blank notes
 * (MEASURE-RULES §5.8). The page Shayna asked for on 2026-10-18; the definitions are the
 * handoff's (touch-drivers-handoff.zip, HANDOFF.md §4) as its `Agg2.cs` computes them.
 *
 * Computed here, in the browser, from Touch detail's rows rather than read from a table:
 * a repeat is a repeat of an earlier touch IN THE PERIOD, and the period is the top bar's
 * month range. A flag stored for one range is wrong for every other, and per-month flags
 * added up lose every repeat of an earlier month's touch.
 *
 * Everything below works on integer columns, so the same functions run on the app's rows
 * (touchDriverRows.ts) and on the prototype's own embedded rows (the reconciliation test).
 *
 *   touch  — one row (an ACTION row of FactClaimTouches, as Touch detail lists them)
 *   claim  — facility + visit
 *   step   — status + action, both trimmed
 */

/** Per-row flag bits, as the prototype's `fl` column (its 8, "repeated group", is unused). */
export const REPEAT = 1
export const DUP = 2
export const BLANK = 4

export interface DriverRows {
  n: number
  person: ArrayLike<number>
  claim: ArrayLike<number>
  /** Trimmed status, as an id. */
  status: ArrayLike<number>
  /** Trimmed action, as an id. */
  action: ArrayLike<number>
  /** Any integer that orders the dates: days since an epoch. */
  day: ArrayLike<number>
  /**
   * 1 for a row in the range; 0 for a look-back row, read only to tell whether a row in
   * the range repeats it. Absent: every row is in the range.
   */
  counted?: ArrayLike<number>
  /**
   * A row's duplicate signature — person, claim, date, RAW status and action, trimmed
   * note. Equal strings are identical rows. Absent: no duplicate test (the prototype's
   * rows carry the flag, not the note).
   */
  sig?: (i: number) => string
  /** 1 where the trimmed note is shorter than 5 characters. */
  blank?: ArrayLike<number>
}

/** One person + claim + step with three or more touches in the range. */
export interface Loop {
  person: number
  claim: number
  status: number
  action: number
  /** Touches in the range. */
  n: number
  /** Days from its first touch to its last, both counted: 1 for a single day. */
  span: number
}

/** A claim with five or more touches in the range and one status throughout. */
export interface Stuck {
  claim: number
  status: number
  n: number
  /** Who touched it most; a tie goes to whoever touched it first. */
  owner: number
}

export interface Drivers {
  /** Flag bits per row (REPEAT | DUP | BLANK); look-back rows are 0. */
  flags: Uint8Array
  loops: Loop[]
  stuck: Stuck[]
}

export const LOOP_MIN = 3
export const STUCK_MIN = 5
/** A note shorter than this, after trimming, is blank. */
export const BLANK_NOTE_MIN = 5

/** Rows in date order; a stable sort, so same-day rows keep the file's order (Agg2's OrderBy). */
function dateOrder(r: DriverRows): Int32Array {
  const idx = Int32Array.from({ length: r.n }, (_, i) => i)
  const day = r.day
  return idx.sort((a, b) => day[a]! - day[b]! || a - b)
}

/**
 * The flags, loops and stuck claims for one range.
 *
 * - **Repeat:** same person, claim, status and action as an earlier row (date order),
 *   look-back rows included. The first occurrence is not a repeat.
 * - **Duplicate:** the same signature as an earlier row in the range. A duplicate is
 *   also a repeat.
 * - **Loop:** person + claim + step with ≥ 3 rows in the range.
 * - **Stuck:** a claim with ≥ 5 rows in the range, all one status. Its owner is the
 *   person with most of them. Over every row passed in: filter by owner afterwards, so a
 *   claim worked across departments is judged on all of its touches (the prototype's
 *   `stuckFor`).
 */
export function computeDrivers(r: DriverRows): Drivers {
  const order = dateOrder(r)
  const counted = r.counted
  const flags = new Uint8Array(r.n)
  const seenStep = new Set<string>()
  const seenSig = new Set<string>()
  const groups = new Map<string, { person: number; claim: number; status: number; action: number; n: number; first: number; last: number }>()
  // Per claim: rows, its first status, whether it ever changed.
  const claimN = new Map<number, number>()
  const claimStatus = new Map<number, number>()
  const claimMoved = new Set<number>()

  for (const i of order) {
    const p = r.person[i]!
    const c = r.claim[i]!
    const s = r.status[i]!
    const a = r.action[i]!
    const key = `${p},${c},${s},${a}`
    const repeat = seenStep.has(key)
    if (!repeat) seenStep.add(key)
    if (counted && !counted[i]) continue

    let f = repeat ? REPEAT : 0
    if (r.sig) {
      const sig = r.sig(i)
      if (seenSig.has(sig)) f |= DUP
      else seenSig.add(sig)
    }
    if (r.blank?.[i]) f |= BLANK
    flags[i] = f

    const d = r.day[i]!
    const g = groups.get(key)
    if (g) {
      g.n++
      g.last = d
    } else groups.set(key, { person: p, claim: c, status: s, action: a, n: 1, first: d, last: d })

    claimN.set(c, (claimN.get(c) ?? 0) + 1)
    const s0 = claimStatus.get(c)
    if (s0 === undefined) claimStatus.set(c, s)
    else if (s0 !== s) claimMoved.add(c)
  }

  const loops: Loop[] = []
  for (const g of groups.values())
    if (g.n >= LOOP_MIN)
      loops.push({ person: g.person, claim: g.claim, status: g.status, action: g.action, n: g.n, span: g.last - g.first + 1 })

  const stuckClaims = new Set<number>()
  for (const [c, n] of claimN) if (n >= STUCK_MIN && !claimMoved.has(c)) stuckClaims.add(c)
  // Who touched each most, in first-touch order so a tie goes to the first.
  const tally = new Map<number, Map<number, number>>()
  for (const i of order) {
    if (counted && !counted[i]) continue
    const c = r.claim[i]!
    if (!stuckClaims.has(c)) continue
    let m = tally.get(c)
    if (!m) tally.set(c, (m = new Map()))
    const p = r.person[i]!
    m.set(p, (m.get(p) ?? 0) + 1)
  }
  const stuck: Stuck[] = []
  for (const [c, m] of tally) {
    let owner = -1
    let most = 0
    for (const [p, k] of m) if (k > most) (owner = p), (most = k)
    stuck.push({ claim: c, status: claimStatus.get(c)!, n: claimN.get(c)!, owner })
  }
  return { flags, loops, stuck }
}

/** What a group of rows adds up to. */
export interface Tally {
  touches: number
  repeats: number
  dups: number
  blanks: number
  /** Distinct claims. */
  claims: number
  /** Distinct days with a touch: "per day" counts only days the person logged one. */
  days: number
}

const EMPTY = (): { t: number; r: number; dup: number; bl: number; cl: Set<number>; dy: Set<number> } => ({
  t: 0,
  r: 0,
  dup: 0,
  bl: 0,
  cl: new Set(),
  dy: new Set(),
})

/**
 * Rows grouped by `keyOf` (a number per row; -1 leaves it out), over the rows `keep`
 * allows and only rows in the range. Each group's touches, repeats, duplicates, blank
 * notes, distinct claims and distinct days.
 */
export function tallyBy(
  r: DriverRows,
  d: Drivers,
  keep: (i: number) => boolean,
  keyOf: (i: number) => number,
): Map<number, Tally> {
  const acc = new Map<number, ReturnType<typeof EMPTY>>()
  for (let i = 0; i < r.n; i++) {
    if (r.counted && !r.counted[i]) continue
    if (!keep(i)) continue
    const k = keyOf(i)
    if (k < 0) continue
    let b = acc.get(k)
    if (!b) acc.set(k, (b = EMPTY()))
    const f = d.flags[i]!
    b.t++
    if (f & REPEAT) b.r++
    if (f & DUP) b.dup++
    if (f & BLANK) b.bl++
    b.cl.add(r.claim[i]!)
    b.dy.add(r.day[i]!)
  }
  const out = new Map<number, Tally>()
  for (const [k, b] of acc)
    out.set(k, { touches: b.t, repeats: b.r, dups: b.dup, blanks: b.bl, claims: b.cl.size, days: b.dy.size })
  return out
}

/** Everything `keep` allows, as one group. */
export function tallyAll(r: DriverRows, d: Drivers, keep: (i: number) => boolean): Tally {
  return tallyBy(r, d, keep, () => 0).get(0) ?? { touches: 0, repeats: 0, dups: 0, blanks: 0, claims: 0, days: 0 }
}

/** Repeats ÷ touches; null with no touches. */
export const repeatRate = (t: Tally): number | null => (t.touches ? t.repeats / t.touches : null)
/** Touches ÷ distinct claims; null with no claims. */
export const touchesPerClaim = (t: Tally): number | null => (t.claims ? t.touches / t.claims : null)

/**
 * A group's share of every touch in the selection (review #886, both readings): its repeat
 * touches, or all its touches, over `total`. Null with no touches at all.
 */
export const shareOfTouches = (t: Tally, total: number, of: 'repeats' | 'touches'): number | null =>
  total ? (of === 'repeats' ? t.repeats : t.touches) / total : null

/** The repeat-rate pill's band: under 25% good, 25–40% amber, 40% and up red. */
export function repeatBand(rate: number | null): 'good' | 'warn' | 'crit' | null {
  if (rate === null) return null
  return rate >= 0.4 ? 'crit' : rate >= 0.25 ? 'warn' : 'good'
}

/** Touches per claim against the page's: under 1.15× good, 1.15–1.5× amber, 1.5× and up red. */
export function perClaimBand(v: number | null, base: number | null): 'good' | 'warn' | 'crit' | null {
  if (v === null || base === null || base === 0) return null
  return v >= base * 1.5 ? 'crit' : v >= base * 1.15 ? 'warn' : 'good'
}

/**
 * The action only restates the status (status "Pending - Family Income", action
 * "Pending"): the touch records nothing new. The handoff's test: the status starts with
 * the action's text.
 */
export const restatesStatus = (status: string, action: string): boolean =>
  action !== '' && status.toLowerCase().startsWith(action.toLowerCase())

/** People who leave this many notes blank, out of at least this many touches, are flagged. */
export const THIN_NOTES = { minTouches: 200, share: 0.1 }
